import { Component, OnInit } from '@angular/core';
import { Observable, BehaviorSubject } from 'rxjs';
import { HoudiniPhotospheresService } from 'houdini-photospheres/dist/houdini-photospheres';
// import { HoudiniPhotospheresService } from 'houdini-photospheres-local'; // For local version
import { Router, NavigationEnd } from '@angular/router';
import { UtilsService } from './services/utils.service';
import { Photosphere } from 'houdini-photospheres/dist/houdini-photospheres';
// import { Photosphere } from 'houdini-photospheres-local'; // For local version
import { DomSanitizer } from '@angular/platform-browser';

declare const window: any;

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss']
})

export class AppComponent implements OnInit {
    // Manually set background image
    public backgroundImageSubject: BehaviorSubject<any>;

    // Previous background image
    public currentBgImg: any = '';

    // Passed along to Houdini Photospheres component for loading scene data
    public photospheresDataObservable!: Observable<any>;

    // Skip executing the next mouseleave event for a hotspot
    // Prevents background image swap confusion
    public skipNextLeaveEvent: boolean = false;

    // Audio toggle - true if enabled
    public audioEnabled: boolean = false;

    // Gyroscope enabled
    public gyroEnabled = false;

    // If browser supports motion controls
    public motionControlsPossible = false;

    // If user has given motion controls permission
    public motionControlsPermitted = false;

    // Instructions modal
    public showInstructions: boolean = false;

    // AR
    public arActive = false;
    public usdzUrl = '../assets/ar/houdinirex.usdz';
    public gltfUrl: any = '../assets/ar/houdinirex.glb';
    public AR_Image: any = '/assets/images/ar_image.png';
    public isAndroid: boolean = false;
    public isIOS: boolean = false;
    public isQuickLookSupported: boolean = false;

    constructor(
        private photosphereSvc: HoudiniPhotospheresService,
        private router: Router,
        private utilsSvc: UtilsService,
        public sanitizer: DomSanitizer
    ) {
        this.backgroundImageSubject = this.utilsSvc.backgroundImageSubject;
    }

    ngOnInit(): void {
        this.subscribe();
        this.utilsSvc.initAudio('main');
        this.utilsSvc.initAudio('hotspot');
        this.utilsSvc.initAudio('t-rex');
        this.loadData();
        this.detectMobileOS();
    }

    /**
     * Subscribe to events
     */
    public subscribe(): void {
        // Listen to router Events
        this.router.events.subscribe((val) => {
            if (val instanceof NavigationEnd) {
                if (val.url !== val.urlAfterRedirects) {
                    location.reload();
                } else {
                    this.utilsSvc.playAudio('main', 'wav', 0.1);
                }
            }
        });

        // Expose current photosphere to other components in this project
        // Also add the current photosphere as a class to the <body> element
        this.photosphereSvc.currentPhotosphere.subscribe((data: Photosphere | null) => {
            this.utilsSvc.currentPhotosphere.next(data);
            this.setBodyClass(data);
        });

        // Expose scene/photosphere/modal/router data to other components in this project
        this.photosphereSvc.dataSubject.subscribe((data) => {
            this.utilsSvc.dataSubject.next(data);
        });

        this.photosphereSvc.photosphereLoaded.subscribe(loaded => {
            if (loaded) {
                this.openInstructions();
            }
        });

        // Listen to service to allow audio
        this.utilsSvc.audioEnabled.subscribe(enabled => {
            this.audioEnabled = enabled;
            this.utilsSvc.playAudio('main', 'wav', 0.1);
        });
    }

    public openInstructions(): void {
        this.showInstructions = true;
        setTimeout(() => {
            this.photosphereSvc.setInteractable(false);
        }, 0);
    }

    public closeInstructions(): void {
        this.showInstructions = false;
        this.photosphereSvc.setInteractable(true);
    }

    /**
     * Detect Mobile OS
     * Checks for android vs ios and sets boolean - used for AR handling
     */
    public detectMobileOS() {
        if (/Android/i.test(navigator.userAgent)) {
            this.isAndroid = true;
            this.isQuickLookSupported = true;
            document.body.classList.add('mobile');
        }

        if (/iPhone|iPad|iPod/i.test(navigator.userAgent)) {
            this.isIOS = true;
            this.isQuickLookSupported = this.utilsSvc.iosQuicklookSupported();
            document.body.classList.add('mobile');

            // __DEBUG
            // this.isQuickLookSupported = true;
        }
    }

    /**
     * Toggle Audio
     * Enable or disable global audio
     */
    public toggleAudio(): void {
        this.utilsSvc.setAudioEnabled(!this.audioEnabled);
    }

    /**
     * Load Data
     * Send local json data to photosphere service to load
     */
    public loadData(): void {
        this.photosphereSvc.loadProject('warehouse', '../assets/data/data.json');
        // this.photosphereSvc.loadProject("hi-warehouse-stage");
    }

    /**
     * Bind additional functionality to hotspot click events
     * @param {any} $event data from hotspot click
     */
    public onHotspotClick($event: any) {
        $event.canceled = true;
        this.closeInstructions();

        if ($event.hotspot.clickType === 'ar') {
            this.arClick();
        } else {
            if ($event?.hotspot && $event.hotspot.id) {
                switch ($event.hotspot.id) {
                    // If clicking on T-Rex mouth, play sound
                    case 'main.trex':
                        this.utilsSvc.playAudio('t-rex', 'wav', 1, false, () => {
                            this.utilsSvc.setVolume('main', 0.1);
                        }, true);

                        this.utilsSvc.setVolume('main', 0.03);
                    break;

                    // Do nothing, retain default behavior.
                    default:
                        this.utilsSvc.playAudio('hotspot', 'wav');

                        setTimeout(() => {
                            if ($event.hotspot.clickUrl && $event.hotspot.clickType === 'internal') {
                                this.router.navigateByUrl($event.hotspot.clickUrl);
                            } else {
                                window.open($event.hotspot.clickUrl);
                            }
                        }, 350);
                    break;
                }
            }
        }
    }

    /**
     * Hotspot hover: update photosphere image
     * @param {any} $event data from hotspot click
     */
    public onHotspotEnter($event: any) {
        if (!this.utilsSvc.isMobile.getValue()) {
            if ($event?.hotspot && $event.hotspot?.extra && $event.hotspot.extra?.overlayPhotosphere) {
                this.photosphereSvc.updateOverlayPhotosphere({
                    id: $event.hotspot.extra.overlayPhotosphere,
                    opacity: 1,
                    animate: true,
                    duration: 0.35
                });
            }
        }
    }

    /**
     * Hotspot stop hover: revert to photosphere image
     * @param $event
     */
    public onHotspotLeave($event: any) {
        if (!this.utilsSvc.isMobile.getValue()) {
            if (this.skipNextLeaveEvent) {
                this.skipNextLeaveEvent = false;
            } else {
                if ($event?.hotspot && $event.hotspot?.extra && $event.hotspot.extra?.overlayPhotosphere) {
                    this.photosphereSvc.updateOverlayPhotosphere({
                        id: $event.hotspot.extra.overlayPhotosphere,
                        opacity: 0,
                        animate: true,
                        duration: 0.35
                    });
                }
            }
        }
    }

    /**
     * Set Body Class
     * @param {Photosphere} photosphere
     */
    public setBodyClass(photosphere: Photosphere | null) {
        let toRemove = '';

        document.body.classList.forEach((className: string) => {
            if (!toRemove && className.indexOf('ps-') === 0) {
                toRemove = className;
            }
        });

        if (toRemove) {
            document.body.classList.remove(toRemove);
        }

        if (photosphere?.id) {
            document.body.classList.add('ps-' + photosphere.id);
        }
    }

    /**
     * AR Click
     * Handle AR link for ios or android
     */
    public arClick(): void {
        this.arActive = true;
        let anchor = document.createElement('a');
        anchor.setAttribute('rel', 'ar');
        anchor.appendChild(document.createElement('img'));

        if (this.isIOS) {
            anchor.setAttribute('href', this.usdzUrl);
        }

        if (this.isAndroid) {
            let newGltfUrl = `intent://arvr.google.com/scene-viewer/1.0?file=${this.gltfUrl}#Intent;scheme=https;package=com.google.ar.core;action=android.intent.action.VIEW;S.browser_fallback_url=https://developers.google.com/ar;end;`;
            anchor.setAttribute('href', newGltfUrl);
        }

        if (this.isIOS || this.isAndroid) {
            anchor.click();
        }
    }
}
